import * as React from "react";
import { Autocomplete } from "@react-google-maps/api";
import Title from "../../../../../common/title/title";
import MapComponent from "../../../../../common/google_map/google_map";
import { DroneDTO } from "../../../../../store/drone_slice";
import { useGoogleMaps } from "../../../../../contexts/google_map_context";

interface LocationProps {
  droneData: Omit<DroneDTO, "id" | "postedBy">;
  updateDroneData: (newData: Partial<Omit<DroneDTO, "id" | "postedBy">>) => void;
}

const Location: React.FC<LocationProps> = ({ droneData, updateDroneData }) => {
  const { isLoaded, loadError } = useGoogleMaps();
  const [autocomplete, setAutocomplete] =
    React.useState<google.maps.places.Autocomplete | null>(null);

  const onLoad = (autocompleteInstance: google.maps.places.Autocomplete) => {
    setAutocomplete(autocompleteInstance);
  };

  const onPlaceChanged = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry && place.geometry.location) {
        const newLocation = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          location: place.formatted_address || "",
        };
        updateDroneData(newLocation);
      }
    }
  };

  const handleMarkerPositionChange = async (
    newPosition: google.maps.LatLngLiteral,
  ) => {
    updateDroneData({
      lat: newPosition.lat,
      lng: newPosition.lng,
    });

    // Reverse geocode to update 'location' field
    if (window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: newPosition }, (results, status) => {
        if (
          status === "OK" &&
          results &&
          results[0] &&
          results[0].formatted_address
        ) {
          updateDroneData({
            location: results[0].formatted_address,
          });
        }
      });
    }
  };

  if (loadError) {
    return <div>Error loading Google Maps API: {loadError.message}</div>;
  }

  if (!isLoaded) {
    return <div>Loading Google Maps API...</div>;
  }

  return (
    <div className="max-w-2xl mx-auto mt-8">
      <Title
        title="Location"
        subtitle="Choose an approximate location where you can rent the drone"
      />
      <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
        <input
          type="text"
          placeholder="Enter a location"
          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </Autocomplete>
      <div className="mt-4">
        <MapComponent
          center={
            droneData.lat !== 0 && droneData.lng !== 0
              ? { lat: droneData.lat, lng: droneData.lng }
              : { lat: 45.9432, lng: 24.9668 } // Romania's geographical center
          }
          zoom={droneData.lat !== 0 && droneData.lng !== 0 ? 14 : 7}
          {...(droneData.lat !== 0 && droneData.lng !== 0
            ? { markerPosition: { lat: droneData.lat, lng: droneData.lng } }
            : {}
          )}
          onMarkerPositionChange={handleMarkerPositionChange}
        />
      </div>
    </div>
  );
};

export default Location;
